import classnames from 'classnames';
import type { FC } from 'react';
import React, { useContext } from 'react';

import {
  RicosAlignmentContext,
  RicosDirectionHandledContext,
  RicosNodeContext,
  RicosNodeContextProvider,
  RicosViewerContext,
} from './../../../../../ricos-context';
import type { NodeStyle } from './../../../../../ricos-schema/legacy/wix/rich_content/v1';
import { TextStyle_TextAlignment } from './../../../../../ricos-schema/legacy/wix/rich_content/v1';
import type { NodeViewRenderer, NodeViewRendererProps } from './../../../../../ricos-types';

import alignmentStyle from '../../../../statics/alignment.rtlignore-scss';
import indentStyle from '../../../../statics/indentation-scss';
import Anchorable from '../../utils/Anchorable';
import { getPaywallSeoClass, isPaywallSeo } from '../../utils/paywallSeo';
import { indentLevelToClassName } from '../consts';
import styles from './heading-scss';

export const levelToClassName = {
  1: 'headerOne',
  2: 'headerTwo',
  3: 'headerThree',
  4: 'headerFour',
  5: 'headerFive',
  6: 'headerSix',
};

const levelToTag = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
} as const;

const FALLBACK_LEVEL = 1;

const Heading: FC<NodeViewRendererProps> = ({ node, nodeIndex, RicosNodesRenderer }) => {
  const { seoMode } = useContext(RicosViewerContext);
  const { textAlignment: parentAlignment } = useContext(RicosAlignmentContext);
  const { parentNode } = useContext(RicosNodeContext);
  const directionHandled = useContext(RicosDirectionHandledContext);
  const { level = 0, textStyle, indentation = 0 } = node.headingData || {};
  const { textAlignment, lineHeight } = textStyle || {};
  const headingClassName = classnames(
    styles[
      levelToClassName[level as $TSFixMeKey<typeof levelToClassName>] ??
        levelToClassName[FALLBACK_LEVEL]
    ],
    alignmentStyle[textAlignment || parentAlignment || TextStyle_TextAlignment.AUTO],
    indentStyle[indentLevelToClassName[indentation as $TSFixMeKey<typeof indentLevelToClassName>]],
    !parentNode && isPaywallSeo(seoMode) && getPaywallSeoClass(seoMode?.paywall || {}, nodeIndex)
  );
  const childClassName = classnames(
    styles.child,
    textAlignment === TextStyle_TextAlignment.JUSTIFY && alignmentStyle[textAlignment]
  );

  const Tag = levelToTag[level as $TSFixMeKey<typeof levelToTag>] ?? levelToTag[FALLBACK_LEVEL];
  const nodeStyle: NodeStyle & { lineHeight?: string } = node.style || {};
  lineHeight && (nodeStyle.lineHeight = lineHeight);

  const isEmpty = !node.nodes?.length;
  const doesEndWithSoftLine =
    !isEmpty && node.nodes?.[node.nodes.length - 1]?.textData?.text?.slice(-1) === '\n';
  const Content = isEmpty ? (
    <br role="presentation" />
  ) : (
    <RicosNodeContextProvider parentNode={node} parentIndex={nodeIndex}>
      <RicosNodesRenderer key={`children-${node.id}`} nodes={node.nodes ?? []} />
    </RicosNodeContextProvider>
  );

  return (
    <Anchorable nodeId={node.id}>
      <Tag className={headingClassName} style={nodeStyle} dir={directionHandled ? '' : 'auto'}>
        <span className={childClassName}>{Content}</span>
        {doesEndWithSoftLine && <br role="presentation" />}
      </Tag>
    </Anchorable>
  );
};

const HeadingRenderer: NodeViewRenderer = ({ node, ...props }) => {
  return <Heading key={node.id} node={node} {...props} />;
};

export default HeadingRenderer;
